// 1. React
import { memo, useEffect, useState } from "react";

// 2. Tipovi
import { VehicleInvoiceRentEditProps } from "./types";
import { FleetVehicleDetailFormValues } from "services/api/fleet/vehicles/types";
import { InvoiceRentEditFormValues, InvoiceOutgoingStatusHistory } from "services/api/fleet/vehicles/invoices/types";

// 3. API / Konstante
import {
    getFleetVehicleInvoiceRentAPI,
    getFleetVehicleInvoiceRentPdfPreviewAPI,
    getInvoiceGenerateNumberAPI,
    getInvoiceIsAvailableAPI,
    toggleInvoiceRentStatusHistoryAPI,
} from "services/api/fleet/vehicles/invoices/api";
import { InvoiceStatusId } from "services/api/fleet/vehicles/invoices/consts";
import { initialFleetVehicleInvoiceRentEditForm } from "services/api/fleet/vehicles/invoices/consts";
import { initialFleetVehicleDetailForm } from "services/api/fleet/vehicles/consts";
import { transformFleetVehicleInvoiceRentEditForm } from "services/api/fleet/vehicles/invoices/transformations";

// 4. UI Komponente
import Tabs from "ui/components/Tabs/Tabs";
import { Modal, SuccessOverlay } from "ui/components/Modal";
import DocumentStatusIntro from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/DocumentStatusIntro/DocumentStatusIntro";
import { Loader } from "ui/components/Loader";
import FeatureUnavailable from "ui/components/Availability/FeatureUnavailable/FeatureUnavailable";
import { useAvailability } from "services/api/hooks/useAvailability";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import successAnimation from "ui/theme/animations/success.json";
import {
    FLEET_VEHICLE_INVOICE_CREATE_PAGE_TITLE,
    FLEET_VEHICLE_INVOICE_EDIT_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// 5. Lokalne komponente
import VehicleInvoiceInfo from "./components/VehicleInvoiceInfo/InvoiceInfo";
import InvoiceInternalNotes from "./components/InvoiceInternalNotes/InvoiceInternalNotes";
import InvoiceSettings from "./components/InvoiceSettings/InvoiceSettings";
import InvoiceConnection from "./components/InvoiceConnection/InvoiceConnection";
import InvoiceStatusToolbar from "./components/InvoiceStatusToolbar";
import { useTranslate } from "helpers";


const buildVehicleLabel = (vehicle: FleetVehicleDetailFormValues) => {
    const label = [
        vehicle.vehicleManufacturer?.name,
        vehicle.vehicleModel?.name,
        vehicle.vehicleType?.name,
    ].filter(Boolean).join(" ").toLowerCase();

    return label.charAt(0).toUpperCase() + label.slice(1);
};

const VehicleInvoiceRentEditCard: React.FC<VehicleInvoiceRentEditProps> = (props) => {
    const { show, vehicleId, invoiceId, contractRentId, onSuccess, onClose } = props;

    const { t }= useTranslate();

    const [formValues, setFormValues] = useState<InvoiceRentEditFormValues>(initialFleetVehicleInvoiceRentEditForm);
    const [vehicle, setVehicle] = useState<FleetVehicleDetailFormValues>(initialFleetVehicleDetailForm);
    const [activeTab, setActiveTab] = useState(0);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    // Intro animacija — prikazuje se uvijek (i za nove i za edit), gasi se tek kad se pojavi PDF
    const [showIntro, setShowIntro] = useState<boolean>(true);
    const [hasAvailableContracts, setHasAvailableContracts] = useState<boolean>(true);
    const [showSuccessOverlay, setShowSuccessOverlay] = useState<boolean>(false);
    // PDF pregled — podignuto iz InvoiceInfo da parent može dohvatiti PDF pri otvaranju
    const [pdfUrl, setPdfUrl] = useState<string>("");
    const [showPdf, setShowPdf] = useState<boolean>(false);

    // Provjera dostupnosti značajke računa (jesu li postavke konfigurirane)
    const { isAvailable: isSettingsAvailable, renderUnavailable } = useAvailability({
        apiFn: getInvoiceIsAvailableAPI,
        enabled: show && !invoiceId,
    });
    const [statusHistory, setStatusHistory] = useState<InvoiceOutgoingStatusHistory[]>([]);
    const [currentStatusId, setCurrentStatusId] = useState<string | null>(null);

    // Resetiranje kompletnog stanja kad se modal otvori/zatvori ili promijeni račun
    const resetState = () => {
        setFormValues({
            ...initialFleetVehicleInvoiceRentEditForm,
            vehicleId: vehicleId || null,
            contractRentId: contractRentId || null,
        });
        setVehicle(initialFleetVehicleDetailForm);
        setActiveTab(0);
        setShowSuccessOverlay(false);
        setPdfUrl("");
        setShowPdf(false);
        setStatusHistory([]);
        setCurrentStatusId(null);
    };

    // Učitavanje postojećeg računa s poviješću statusa + odmah prikaži PDF pregled
    const loadExistingInvoice = async (id: string) => {
        setIsLoading(true);

        const result = await getFleetVehicleInvoiceRentAPI(id);
        const formData = transformFleetVehicleInvoiceRentEditForm(result);
        setFormValues(formData);
        if (result?.vehicle) setVehicle(result.vehicle);
        setStatusHistory(result.statusHistory ?? []);
        setCurrentStatusId(result.status?.id ?? null);

        // Dohvati PDF pregled prije gašenja loadera — sve se pojavi odjednom (kao kod ugovora)
        const url = await getFleetVehicleInvoiceRentPdfPreviewAPI(formData);
        if (url) {
            setPdfUrl(url);
            setShowPdf(true);
        }

        setIsLoading(false);
    };

    // Promjena statusa u toolbaru (Poslan, Plaćen)
    const handleStatusToggle = async (statusId: string, checked: boolean) => {
        if (!formValues.id) return;

        try {
            const result = await toggleInvoiceRentStatusHistoryAPI(formValues.id, statusId, checked);
            setStatusHistory(result.statusHistory ?? []);
            setCurrentStatusId(result.status?.id ?? null);
        } catch {
            // greška se prikazuje putem snackbara
        }
    };

    // Generiranje novog broja računa za nove račune
    const loadNewInvoice = async () => {
        const number = await getInvoiceGenerateNumberAPI();
        if (number) setFormValues(prev => ({ ...prev, invoiceNumber: number }));
    };

    // Učitavanje podataka računa kad se modal otvori
    useEffect(() => {
        if (!show) return;
        resetState();

        setShowIntro(true);

        if (invoiceId) {
            loadExistingInvoice(invoiceId).catch(() => { });
        } else {
            setIsLoading(true);
            loadNewInvoice().catch(() => { });
        }
    }, [show, invoiceId]);

    // Loader ostaje aktivan dok se ne završi odabir ugovora i dohvat ponude;
    // gasi se odmah samo ako nema dostupnih ugovora
    const handleContractRentsLoaded = (count: number) => {
        setHasAvailableContracts(count > 0);
        if (count === 0) setIsLoading(false);
    };

    // Nakon zatvaranja success overlaya — dohvati PDF pregled (isti tok kao kod ugovora)
    const handleSuccessDismiss = async () => {
        setShowSuccessOverlay(false);

        const invoiceId = formValues.id;
        if (invoiceId) {
            const url = await getFleetVehicleInvoiceRentPdfPreviewAPI(formValues);
            if (url) {
                setPdfUrl(url);
                setShowPdf(true);
            }
        }
    };

    const baseTitle = invoiceId
        ? FLEET_VEHICLE_INVOICE_EDIT_PAGE_TITLE.text
        : FLEET_VEHICLE_INVOICE_CREATE_PAGE_TITLE.text;

    const title = formValues.invoiceNumber
        ? `${t(baseTitle)} ${formValues.invoiceNumber}`
        : t(baseTitle);

    return (
        <>
        <Modal
            show={show}
            onHide={onClose}
            position="side"
            size="medium"
            intro={showIntro ? {
                content: (
                    <DocumentStatusIntro
                        documentType="invoice"
                        isRent
                        statusId={currentStatusId}
                    />
                ),
            } : undefined}
        >
            <ModalHeader
                icon="/media/images/add-icon.svg"
                title={t(title)}
                onHide={onClose}
            />
            <ModalBody>
                {isLoading && (
                    <div className="relative h-[calc(100vh-80px)]">
                        <Loader isLoading={true}>{null}</Loader>
                    </div>
                )}
                    <form noValidate onSubmit={(e) => e.preventDefault()} className={isLoading ? "hidden" : ""}>
                        {/* Statusna traka — prikazuje napredak računa (Predložak → Kreiran → Fiskaliziran → Poslan → Plaćen) */}
                        <div className="px-4 mt-6">
                            <InvoiceStatusToolbar
                                statusHistory={statusHistory}
                                onToggle={handleStatusToggle}
                                disabled={currentStatusId === InvoiceStatusId.Cancelled}
                                fiscalizationStatus={formValues.fiscalizationStatus}
                                invoiceStatusId={formValues.invoiceStatusId}
                            />
                        </div>
                        {/* Provjera dostupnosti značajke */}
                        {renderUnavailable({ title: "Račun nije dostupan", onClose })}
                        <FeatureUnavailable
                            show={isSettingsAvailable && !hasAvailableContracts && !invoiceId && !isLoading}
                            title="Nema dostupnih ugovora"
                            description="Za kreiranje računa potrebno je imati barem jedan aktivni ugovor koji nije vezan na postojeći račun. Molimo kreirajte prvo ugovor."
                            onClose={onClose}
                        />
                        <div className={(!isSettingsAvailable || !hasAvailableContracts) && !invoiceId ? "hidden" : ""}>
                        {/* Glavni obrazac računa s tabovima */}
                        <div className="flex mt-4 mb-6 px-4 min-h-[70vh] 2xl:min-h-[1000px]">
                            <Tabs
                                activeIndex={activeTab}
                                onTabChange={setActiveTab}
                                tabs={[
                                    {
                                        label: "Osnovne informacije",
                                        content: (
                                            <div className="px-4 pt-3 pb-4 border border-slate-200 -mt-[1px]">
                                                <VehicleInvoiceInfo
                                                    vehicleId={formValues.vehicleId || ""}
                                                    formValues={formValues}
                                                    setFormValues={setFormValues}
                                                    onSuccess={onSuccess}
                                                    onClose={onClose}
                                                    onFinish={() => setShowSuccessOverlay(true)}
                                                    onStatusHistoryChange={(history, statusId) => {
                                                        setStatusHistory(history);
                                                        setCurrentStatusId(statusId ?? null);
                                                    }}
                                                    onSetParentLoading={setIsLoading}
                                                    onContractRentsLoaded={handleContractRentsLoaded}
                                                    hasIntro={showIntro}
                                                    pdfUrl={pdfUrl}
                                                    setPdfUrl={setPdfUrl}
                                                    showPdf={showPdf}
                                                    setShowPdf={setShowPdf}
                                                />
                                            </div>
                                        ),
                                    },
                                    {
                                        label: "Poveznice računa",
                                        content: (
                                            <div className="px-4 py-4 border border-slate-200 -mt-[1px]">
                                                <InvoiceConnection
                                                    formValues={formValues}
                                                    setFormValues={setFormValues}
                                                />
                                            </div>
                                        ),
                                    },

                                     {
                                        label: "Interna napomena",
                                        content: (
                                            <div className="px-4 py-4 border border-slate-200 -mt-[1px]">
                                                <InvoiceInternalNotes
                                                    invoiceId={formValues.id || undefined}
                                                />
                                            </div>
                                        ),
                                    },
                                       {
                                        label: "Postavke",
                                        content: (
                                            <div className="px-4 py-4 border border-slate-200 -mt-[1px]">
                                                <InvoiceSettings
                                                    formValues={formValues}
                                                    setFormValues={setFormValues}
                                                />
                                            </div>
                                        ),
                                    },
                                ]}
                            />
                        </div>
                        </div>
                    </form>
            </ModalBody>
        </Modal>
        {/* Overlay uspješnog kreiranja računa */}
        <SuccessOverlay
            show={showSuccessOverlay}
            animation={successAnimation}
            title="Račun je uspješno kreiran!"
            description="Možete ga pregledati ili poslati klijentu"
            onDismiss={handleSuccessDismiss}
        />
        </>
    );
};
export default memo(VehicleInvoiceRentEditCard);
